import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { DashboardSummary } from '../types';
import { Card } from '@shared/components/ui/Card';
import { useTheme } from '@shared/contexts/ThemeContext';
import { getStatusTone, type Tone } from '@shared/config/theme';
import {
  toneChipClasses,
  toneDotClasses,
  toneIconBgClasses,
  toneTextClasses,
  vizColor,
} from '@shared/utils/statusTone';

interface AdvancedDashboardProps {
  dashboardData: DashboardSummary;
  setBackupNowModalOpen?: (isOpen: boolean) => void;
  setTimePeriod: (timePeriod: string) => void;
  timePeriod: string;
  selectedAgentId: string | null;
  setSelectedAgentId: (id: string | null) => void;
}

const AdvancedDashboard: React.FC<AdvancedDashboardProps> = ({
  dashboardData,
  setTimePeriod,
  timePeriod,
  selectedAgentId,
  setSelectedAgentId,
}) => {
  const { t, i18n } = useTranslation('dashboard');
  const { theme } = useTheme();

  const healthTone = getStatusTone(dashboardData.system_health.status);
  const locale = i18n.resolvedLanguage || i18n.language;

  const metrics = dashboardData.advanced_metrics;
  if (!metrics) return null;

  const safeAgents = metrics.agents || [];
  const safeSchedules = metrics.schedules || [];
  const safeStorageTrend = metrics.storage_trend || [];
  const backupSuccessDetails = metrics.backup_success_rate_detail;
  const successfulBackups = backupSuccessDetails?.success_count ?? 0;
  const failedBackups = backupSuccessDetails?.failure_count ?? 0;
  const totalBackups = backupSuccessDetails?.total_count ?? successfulBackups + failedBackups;
  const otherBackups = Math.max(0, totalBackups - successfulBackups - failedBackups);
  const agentMetricsData = metrics.agent_metrics_data || {};
  const selectedAgentCpuMetrics = selectedAgentId
    ? (agentMetricsData.cpu?.[selectedAgentId] ?? [])
    : [];
  const selectedAgentMemoryMetrics = selectedAgentId
    ? (agentMetricsData.memory?.[selectedAgentId] ?? [])
    : [];
  const hasSelectedAgentMetrics =
    selectedAgentCpuMetrics.length > 0 || selectedAgentMemoryMetrics.length > 0;
  const safeRecentActivity = metrics.recent_activity_extended || [];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* System Health Card */}
      <div
        className={`rounded-xl border p-6 shadow-sm transition-colors ${toneChipClasses[healthTone]}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div
              className={`p-3 rounded-full bg-white dark:bg-surface-card-dark bg-opacity-50 dark:bg-opacity-40 shadow-inner`}
            >
              <StatusIcon status={dashboardData.system_health.status} />
            </div>
            <div>
              <h3 className="text-xl font-bold">{t('analytics.systemHealth')}</h3>
              <p className="text-sm">
                {dashboardData.system_health.message?.trim().toLowerCase() === 'some agents offline'
                  ? t('analytics.someAgentsOffline')
                  : dashboardData.system_health.message}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-black">
              {dashboardData.system_health.agents_online}/{dashboardData.system_health.agents_total}
            </div>
            <div className="text-[10px] uppercase tracking-widest font-bold">
              {t('analytics.agentsOnline')}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Backup Success Rate */}
        <Card className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h3 className="font-bold text-slate-800 dark:text-slate-200 uppercase tracking-tight text-sm">
              {t('analytics.backupSuccessRate')}
            </h3>
            <PeriodSelector current={timePeriod} onChange={setTimePeriod} />
          </div>
          <div className="flex items-center">
            <div className="w-32 h-32">
              <ResponsiveContainer
                width="100%"
                height="100%"
                initialDimension={{ width: 128, height: 128 }}
              >
                <PieChart>
                  <Pie
                    data={[
                      {
                        name: t('analytics.success'),
                        value: successfulBackups,
                      },
                      {
                        name: t('analytics.failed'),
                        value: failedBackups,
                      },
                      {
                        name: t('analytics.other'),
                        value: otherBackups,
                      },
                    ]}
                    innerRadius={35}
                    outerRadius={50}
                    paddingAngle={5}
                    dataKey="value"
                    stroke="none"
                  >
                    <Cell className="fill-state-ok-light dark:fill-state-ok-dark" />
                    <Cell className="fill-state-danger-light dark:fill-state-danger-dark" />
                    <Cell className="fill-slate-400 dark:fill-slate-500" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="ml-8 flex-1">
              <div className="text-5xl font-black text-slate-900 dark:text-white leading-none mb-2">
                {new Intl.NumberFormat(locale, {
                  style: 'percent',
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                }).format(metrics.backup_success_rate / 100)}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <div>
                  <div className="text-xs text-text-subtle-light dark:text-text-subtle-dark font-bold uppercase">
                    {t('analytics.success')}
                  </div>
                  <div className="text-lg font-bold text-state-ok-light dark:text-state-ok-dark">
                    {new Intl.NumberFormat(locale).format(successfulBackups)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-text-subtle-light dark:text-text-subtle-dark font-bold uppercase">
                    {t('analytics.failed')}
                  </div>
                  <div className="text-lg font-bold text-state-danger-light dark:text-state-danger-dark">
                    {new Intl.NumberFormat(locale).format(failedBackups)}
                  </div>
                </div>
                <div className="col-span-2 flex items-center justify-between border-t border-surface-border-light pt-2 dark:border-surface-border-dark">
                  <div className="text-[10px] font-bold uppercase text-text-subtle-light dark:text-text-subtle-dark">
                    {t('analytics.other')}
                  </div>
                  <div className="text-lg font-bold text-slate-500 dark:text-slate-400">
                    {new Intl.NumberFormat(locale).format(otherBackups)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Storage usage trend */}
        <Card className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h3 className="font-bold text-slate-800 dark:text-slate-200 uppercase tracking-tight text-sm">
              {t('analytics.storageTrend')}
            </h3>
            <PeriodSelector current={timePeriod} onChange={setTimePeriod} />
          </div>
          <div className="h-32 w-full">
            <ResponsiveContainer
              width="100%"
              height="100%"
              initialDimension={{ width: 300, height: 128 }}
            >
              <LineChart data={safeStorageTrend}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="currentColor"
                  className="text-surface-border-light dark:text-surface-border-dark"
                />
                <XAxis dataKey="date" hide />
                <YAxis hide />
                <Tooltip
                  formatter={(value) => [
                    `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(Number(value))} GB`,
                    t('stats.storageUsed'),
                  ]}
                  labelFormatter={(label) => {
                    const date = new Date(`${String(label).slice(0, 10)}T00:00:00Z`);
                    return Number.isNaN(date.getTime())
                      ? label
                      : new Intl.DateTimeFormat(locale, {
                          year: 'numeric',
                          month: 'short',
                          day: '2-digit',
                          timeZone: 'UTC',
                        }).format(date);
                  }}
                  contentStyle={{
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    backgroundColor: 'var(--tooltip-bg)',
                    color: 'var(--tooltip-text)',
                  }}
                  itemStyle={{ fontWeight: 'bold' }}
                />
                <Line
                  type="monotone"
                  dataKey="used_gb"
                  stroke={vizColor('cpu', theme)}
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Agent Metrics Section */}
      <Card className="overflow-hidden">
        <div className="p-6 border-b border-surface-border-light dark:border-surface-border-dark flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3
            id="dashboard-agent-metrics-title"
            className="font-bold text-slate-800 dark:text-slate-200 uppercase tracking-tight text-sm"
          >
            {t('analytics.agentResourceMetrics')}
          </h3>
          <select
            aria-label={t('analytics.agentResourceMetrics')}
            className="h-11 min-h-11 w-full sm:w-auto text-sm font-bold border-surface-border-light dark:border-surface-border-dark rounded-lg bg-surface-2-light dark:bg-surface-2-dark px-3 py-2 outline-none focus:ring-2 focus:ring-brand-500 transition-all text-slate-700 dark:text-slate-300"
            value={selectedAgentId || ''}
            onChange={(e) => setSelectedAgentId(e.target.value || null)}
          >
            <option value="">{t('labels.selectAnAgent')}</option>
            {safeAgents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>

        {selectedAgentId && hasSelectedAgentMetrics ? (
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
            <MetricSmallChart
              title={t('analytics.cpuUsage')}
              data={selectedAgentCpuMetrics}
              color={vizColor('cpu', theme)}
              emptyMessage={t('analytics.noMetricReadings')}
            />
            <MetricSmallChart
              title={t('analytics.memoryUsage')}
              data={selectedAgentMemoryMetrics}
              color={vizColor('io', theme)}
              emptyMessage={t('analytics.noMetricReadings')}
            />
          </div>
        ) : (
          <div className="p-12 text-center text-text-subtle-light dark:text-text-subtle-dark">
            <p className="text-sm font-medium italic">
              {selectedAgentId ? t('analytics.noAgentMetrics') : t('analytics.selectAgentPrompt')}
            </p>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Schedules */}
        <Card className="lg:col-span-2 p-6">
          <h3 className="font-bold text-slate-800 dark:text-slate-200 uppercase tracking-tight text-sm mb-6">
            {t('analytics.upcomingSchedules')}
          </h3>
          <div className="space-y-3">
            {safeSchedules.length > 0 ? (
              safeSchedules.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between p-4 rounded-xl bg-surface-2-light dark:bg-surface-2-dark hover:bg-accent-soft-light dark:hover:bg-accent-soft-dark transition-colors border border-transparent hover:border-brand-100 dark:hover:border-brand-900/50 group"
                >
                  <div className="flex items-center space-x-4">
                    <div
                      className={`w-2 h-2 rounded-full ${s.is_enabled ? `${toneDotClasses.ok} animate-pulse` : toneDotClasses.neutral}`}
                    />
                    <div>
                      <div className="font-bold text-slate-900 dark:text-white group-hover:text-brand-700 dark:group-hover:text-brand-400 transition-colors">
                        {s.config_name}
                      </div>
                      <div className="text-xs text-text-muted-light dark:text-text-muted-dark font-medium">
                        {s.agent_name} •{' '}
                        {t(
                          `backups:alertState.scheduleTypeValue.${s.schedule_type.toLowerCase()}`,
                          {
                            defaultValue: s.schedule_type,
                          }
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-slate-700 dark:text-slate-300">
                      {s.next_run
                        ? new Date(s.next_run).toLocaleTimeString(locale, {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : t('analytics.paused')}
                    </div>
                    <div className="text-[10px] text-text-subtle-light dark:text-text-subtle-dark uppercase font-black">
                      {t('analytics.nextRun')}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center py-8 text-text-subtle-light dark:text-text-subtle-dark italic">
                {t('analytics.noSchedules')}
              </p>
            )}
          </div>
        </Card>

        {/* Activity */}
        <Card className="p-6">
          <h3 className="font-bold text-slate-800 dark:text-slate-200 uppercase tracking-tight text-sm mb-6">
            {t('analytics.recentEvents')}
          </h3>
          <div className="flow-root">
            <ul className="-mb-8">
              {safeRecentActivity.map((activity, idx) => (
                <li key={activity.id}>
                  <div className="relative pb-8">
                    {idx !== safeRecentActivity.length - 1 ? (
                      <span
                        className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-surface-border-light dark:bg-surface-border-dark"
                        aria-hidden="true"
                      />
                    ) : null}
                    <div className="relative flex space-x-3">
                      <div>
                        <ActivityIcon type={activity.type} status={activity.status} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div>
                          <div className="text-xs font-bold text-slate-900 dark:text-white">
                            {translateEventAction(activity.action, t)}
                          </div>
                          <p className="mt-0.5 text-[10px] text-text-muted-light dark:text-text-muted-dark font-medium">
                            {new Date(activity.timestamp).toLocaleString(i18n.resolvedLanguage)}
                          </p>
                        </div>
                        <div className="mt-2 text-xs text-slate-600 dark:text-text-subtle-dark line-clamp-2 leading-relaxed">
                          {activity.message}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Card>
      </div>
    </div>
  );
};

function StatusIcon({ status }: { status: string }) {
  const iconClass = `w-8 h-8 ${toneTextClasses[getStatusTone(status)]}`;
  if (status === 'healthy')
    return (
      <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2.5}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    );
  if (status === 'warning')
    return (
      <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2.5}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    );
  return (
    <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function PeriodSelector({ current, onChange }: { current: string; onChange: (v: string) => void }) {
  return (
    <div className="flex bg-surface-2-light dark:bg-surface-2-dark p-1 rounded-lg border dark:border-surface-border-dark shadow-inner">
      {['7d', '30d', '90d'].map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={`flex min-h-11 min-w-11 items-center justify-center px-3 text-[10px] font-black uppercase tracking-tighter rounded-md transition-all ${current === p ? 'bg-accent-soft-light dark:bg-accent-soft-dark text-accent-text-light dark:text-accent-text-dark shadow-sm' : 'text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-slate-300'}`}
        >
          {p}
        </button>
      ))}
    </div>
  );
}

function translateEventAction(action: string, t: (key: string) => string): string {
  const translations: Record<string, string> = {
    'backup completed': 'activity.backupCompleted',
    'backup failed': 'activity.backupFailed',
    'backup started': 'activity.backupStarted',
    'restore completed': 'activity.restoreCompleted',
    'restore failed': 'activity.restoreFailed',
    'restore requested': 'activity.restoreRequested',
  };
  const key = translations[action.trim().toLowerCase()];
  return key ? t(key) : action;
}

function MetricSmallChart({
  title,
  data,
  color,
  emptyMessage,
}: {
  title: string;
  data: any[];
  color: string;
  emptyMessage: string;
}) {
  return (
    <div>
      <h4 className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase mb-4 tracking-widest">
        {title}
      </h4>
      <div className="h-24">
        {data.length === 0 ? (
          <p className="flex h-full items-center text-sm text-text-muted-light dark:text-text-muted-dark">
            {emptyMessage}
          </p>
        ) : (
          <ResponsiveContainer
            width="100%"
            height="100%"
            initialDimension={{ width: 300, height: 96 }}
          >
            <LineChart data={data}>
              <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function ActivityIcon({ type, status }: { type: string; status?: string }) {
  const tone: Tone = status ? getStatusTone(status) : 'accent';

  return (
    <span
      className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-surface-card-light dark:ring-surface-card-dark ${toneIconBgClasses[tone]}`}
    >
      {type === 'backup' ? (
        <svg
          className={`h-4 w-4 ${toneTextClasses[tone]}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
          />
        </svg>
      ) : (
        <svg
          className={`h-4 w-4 ${toneTextClasses[tone]}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
          />
        </svg>
      )}
    </span>
  );
}

export default AdvancedDashboard;
